import { useCallback, useState } from "react";
import { Camera, PerspectiveCamera as PerspectiveCameraImpl, Raycaster, Vector2 } from "three";
import { CameraType } from "./cameraType";
import { useThree } from "@react-three/fiber";
import { useDebounce } from "@/utils/useDebounce";
import { SELECTED_ITEM_TYPE } from "@/3d/SelectedItem";

export const usePerspectiveCameraCustomControls = (
    camera: CameraType,
    perspectiveCameraRef: React.MutableRefObject<PerspectiveCameraImpl | null>
) => {
    const [perspectiveCameraTarget, setPerspectiveCameraTarget] = useState<
        [x: number, y: number, z: number]
    >([0, 1, 0]);

    const scene = useThree((state) => state.scene);

    const updatePerspectiveCameraTarget = useCallback(() => {
        if (
            camera !== "perspective" ||
            perspectiveCameraRef.current === null ||
            perspectiveCameraRef.current.near === null
        )
            return [];

        const raycaster = new Raycaster();
        raycaster.setFromCamera(new Vector2(0, 0), perspectiveCameraRef.current);

        const intersects = raycaster.intersectObjects(scene.children);

        const selectableObjectIntersects = intersects.filter(
            (i) => "selectedItemType" in i.object.userData
        );

        if (selectableObjectIntersects.length === 0) {
            return;
        }

        if (selectableObjectIntersects.length > 1) {
            const nonWallPropIntersect = selectableObjectIntersects.find(
                (i) => i.object.userData.selectedItemType !== SELECTED_ITEM_TYPE.WALL_PROP
            );

            if (nonWallPropIntersect) {
                setPerspectiveCameraTarget(nonWallPropIntersect.point.toArray());
                return;
            }
        }

        setPerspectiveCameraTarget(selectableObjectIntersects[0].point.toArray());
    }, [camera, scene, perspectiveCameraRef]);

    const debouncedUpdatePerspectiveCameraTarget = useDebounce(updatePerspectiveCameraTarget, 100);

    const handlePerspectiveCameraChange = () => {
        if (perspectiveCameraRef.current != null) {
            restrictCameraPosition(perspectiveCameraRef.current);
            debouncedUpdatePerspectiveCameraTarget();
        }
    };

    return {
        perspectiveCameraTarget,
        handlePerspectiveCameraChange,
    };
};

const restrictCameraPosition = (camera: Camera) => {
    if (camera.position.y < 1) {
        const pos = camera.position;
        camera.position.set(pos.x, 1, pos.z);
    }
};
